/**
 * ContractsOverviewPage - Vue transverse Contrats (tous salariés)
 * Affiche tous les contrats avec filtres par type, statut, échéance CDD
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { 
  FileSignature, Search, Calendar, Users, AlertTriangle,
  Loader2, Clock, CheckCircle, Filter, ChevronRight
} from "lucide-react";
import { formatDateFr } from "../../components/ui/date-picker-fr";
import { getContractTypeLabel, CONTRACT_TYPE_OPTIONS } from "../../lib/hr-constants";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContractsOverviewPage() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  
  const token = localStorage.getItem("token");

  // Fetch all employees and their contracts
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const empResponse = await axios.get(`${API}/api/hr/employees?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const emps = empResponse.data.data || [];
      setEmployees(emps);
      
      // Fetch contracts for each employee
      const allContracts = [];
      for (const emp of emps) {
        try {
          const response = await axios.get(
            `${API}/api/hr/employees/${emp.id}/contracts`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Get contract_type from employee's professional_info
          const empContractType = emp.professional_info?.contract_type || "cdi";
          const empContracts = (response.data.contracts || []).map(c => ({
            ...c,
            contract_type: empContractType,
            doc_type: c.type, // 'initial_contract' or 'amendment'
            start_date: c.effective_date || c.signature_date,
            status: c.end_date && new Date(c.end_date) < new Date() ? "ended" : "active",
            employee: emp
          }));
          allContracts.push(...empContracts);
        } catch (e) {
          // Skip employee if error
        }
      }
      setContracts(allContracts);
      setError(null);
    } catch (err) {
      setError("Erreur lors du chargement des contrats");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate days until end for CDD
  const getDaysUntilEnd = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  };

  // Filter contracts
  const getFilteredContracts = () => {
    let filtered = contracts.filter(c => {
      // Only show main contracts (not amendments)
      if (c.doc_type === "amendment") return false;
      
      if (filterType !== "all" && c.contract_type !== filterType) return false;
      
      if (filterStatus === "active" && c.status !== "active") return false;
      if (filterStatus === "ended" && c.status !== "ended") return false;
      
      if (searchQuery) {
        const emp = c.employee;
        if (!emp) return false;
        const name = `${emp.personal_info?.first_name} ${emp.personal_info?.last_name}`.toLowerCase();
        if (!name.includes(searchQuery.toLowerCase())) return false;
      }
      
      return true;
    });
    
    // Tab filtering
    if (activeTab === "expiring") {
      filtered = filtered.filter(c => {
        const days = getDaysUntilEnd(c.end_date);
        return days !== null && days >= 0 && days <= 90;
      });
    } else if (activeTab === "cdd") {
      filtered = filtered.filter(c => 
        ["cdd", "alternance", "stage", "interim"].includes(c.contract_type)
      );
    }
    
    return filtered;
  };

  const filteredContracts = getFilteredContracts();

  // Sort: expiring first, then by start date
  const sortedContracts = [...filteredContracts].sort((a, b) => {
    const daysA = getDaysUntilEnd(a.end_date);
    const daysB = getDaysUntilEnd(b.end_date);
    
    // Expiring contracts first
    if (daysA !== null && daysB === null) return -1;
    if (daysA === null && daysB !== null) return 1;
    if (daysA !== null && daysB !== null) return daysA - daysB;
    
    // Then by start date
    return new Date(b.start_date) - new Date(a.start_date);
  });

  // Count expiring contracts (within 90 days)
  const expiringCount = contracts.filter(c => {
    if (c.doc_type === "amendment") return false;
    const days = getDaysUntilEnd(c.end_date);
    return days !== null && days >= 0 && days <= 90;
  }).length;

  // Count CDD contracts
  const cddCount = contracts.filter(c => 
    c.doc_type !== "amendment" && 
    ["cdd", "alternance", "stage", "interim"].includes(c.contract_type)
  ).length;

  // Get badge for expiration
  const getExpirationBadge = (endDate) => {
    const days = getDaysUntilEnd(endDate);
    if (days === null) return null;
    
    if (days < 0) {
      return <Badge className="bg-slate-100 text-slate-600">Terminé</Badge>;
    }
    if (days <= 30) {
      return <Badge className="bg-red-100 text-red-700">Expire dans {days}j</Badge>;
    }
    if (days <= 60) {
      return <Badge className="bg-orange-100 text-orange-700">Expire dans {days}j</Badge>;
    }
    if (days <= 90) {
      return <Badge className="bg-amber-100 text-amber-700">Expire dans {days}j</Badge>;
    }
    return null;
  };

  // Contract type badge color
  const getContractTypeBadge = (type) => {
    const colors = {
      cdi: "bg-green-100 text-green-700",
      cdd: "bg-blue-100 text-blue-700",
      alternance: "bg-purple-100 text-purple-700",
      stage: "bg-cyan-100 text-cyan-700",
      interim: "bg-amber-100 text-amber-700",
    };
    return colors[type] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="contracts-overview-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-indigo-600" />
            Contrats
          </h1>
          <p className="text-slate-500 mt-1">Vue transverse de tous les contrats</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500 flex items-center justify-center">
                <FileSignature className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-indigo-600 font-medium">Total contrats</p>
                <p className="text-2xl font-bold text-indigo-900">{filteredContracts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">CDI</p>
                <p className="text-2xl font-bold text-green-900">
                  {contracts.filter(c => c.doc_type !== "amendment" && c.contract_type === "cdi").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">CDD / Autres</p>
                <p className="text-2xl font-bold text-blue-900">{cddCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {expiringCount > 0 && (
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-red-600 font-medium">À échéance (90j)</p>
                  <p className="text-2xl font-bold text-red-900">{expiringCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">
            Tous les contrats
          </TabsTrigger>
          <TabsTrigger value="expiring" className="relative">
            À échéance
            {expiringCount > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{expiringCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="cdd">
            CDD / Temporaires
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtres:</span>
            </div>
            
            <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-4">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Type de contrat" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {CONTRACT_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="active">En cours</SelectItem>
                  <SelectItem value="ended">Terminé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-0 sm:min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table/Cards */}
      <Card>
        <CardContent className="p-0">
          {sortedContracts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <FileSignature className="w-12 h-12 mb-4 text-slate-300" />
              <p>Aucun contrat trouvé</p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="block sm:hidden divide-y divide-slate-100">
                {sortedContracts.map(contract => {
                  const emp = contract.employee;
                  const expirationBadge = getExpirationBadge(contract.end_date);
                  
                  return (
                    <div
                      key={contract.id}
                      className="p-4 active:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/rh/salaries/${contract.employee_id}?tab=contracts`)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm shrink-0">
                            {emp?.personal_info?.first_name?.charAt(0)}{emp?.personal_info?.last_name?.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {emp?.personal_info?.last_name} {emp?.personal_info?.first_name}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              {emp?.professional_info?.job_title}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Badge className={getContractTypeBadge(contract.contract_type) + " text-xs"}>
                          {getContractTypeLabel(contract.contract_type)}
                        </Badge>
                        <Badge variant="outline" className={
                          (contract.status === "active" 
                            ? "bg-green-50 text-green-700 border-green-200" 
                            : "bg-slate-50 text-slate-600 border-slate-200") + " text-xs"
                        }>
                          {contract.status === "active" ? "En cours" : "Terminé"}
                        </Badge>
                        {expirationBadge}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span>Début: {formatDateFr(contract.start_date)}</span>
                        {contract.end_date && <span>Fin: {formatDateFr(contract.end_date)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Salarié</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Début</TableHead>
                      <TableHead>Fin</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedContracts.map(contract => {
                      const emp = contract.employee;
                      const expirationBadge = getExpirationBadge(contract.end_date);
                      
                      return (
                        <TableRow 
                          key={contract.id} 
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/rh/salaries/${contract.employee_id}?tab=contracts`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                                {emp?.personal_info?.first_name?.charAt(0)}{emp?.personal_info?.last_name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  {emp?.personal_info?.last_name} {emp?.personal_info?.first_name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {emp?.professional_info?.job_title}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getContractTypeBadge(contract.contract_type)}>
                              {getContractTypeLabel(contract.contract_type)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatDateFr(contract.start_date)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {contract.end_date ? formatDateFr(contract.end_date) : "—"}
                              {expirationBadge}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              contract.status === "active" 
                                ? "bg-green-50 text-green-700 border-green-200" 
                                : "bg-slate-50 text-slate-600 border-slate-200"
                            }>
                              {contract.status === "active" ? "En cours" : "Terminé"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                navigate(`/rh/salaries/${contract.employee_id}?tab=contracts`);
                              }}
                            >
                              Voir
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
